(() => {
  const SELECTORS = {
    adjustment: '[data-lie-angle-adjustment]',
    select: '[data-lie-angle-select]',
    message: '[data-lie-angle-message]',
    productForm: 'form[action*="/cart/add"]',
    submitButton: '[type="submit"]'
  };

  const STANDARD_VALUE = 'Standard';

  function createReference() {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return [
      Date.now().toString(36),
      Math.random().toString(36).slice(2)
    ].join('-');
  }

  function getCartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function getQuantity(form) {
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = Number.parseInt(quantityInput?.value || '1', 10);

    return Number.isFinite(quantity) && quantity > 0
      ? quantity
      : 1;
  }

  function getVariantId(form) {
    const idInput = form.querySelector('[name="id"]');
    const variantId = Number.parseInt(idInput?.value || '', 10);

    if (!Number.isFinite(variantId) || variantId <= 0) {
      throw new Error(
        'The selected product variant could not be found.'
      );
    }

    return variantId;
  }

  function getClubCount(container) {
    const clubCount = Number.parseInt(
      container.dataset.clubCount || '1',
      10
    );

    return Number.isFinite(clubCount) && clubCount > 0
      ? clubCount
      : 1;
  }

  function getAdjustmentVariantId(container) {
    const variantId = Number.parseInt(
      container.dataset.adjustmentVariantId || '',
      10
    );

    return Number.isFinite(variantId) && variantId > 0
      ? variantId
      : null;
  }

  function getProperties(form) {
    const properties = {};

    form
      .querySelectorAll('[name^="properties["]:not([disabled])')
      .forEach((field) => {
        if (
          (field.type === 'checkbox' ||
            field.type === 'radio') &&
          !field.checked
        ) {
          return;
        }

        const match = field.name.match(/^properties\[(.+)]$/);

        if (!match || field.value === '') {
          return;
        }

        properties[match[1]] = field.value;
      });

    return properties;
  }

  function updateMessage(container) {
    const select = container.querySelector(SELECTORS.select);
    const message = container.querySelector(SELECTORS.message);
    const clubCount = getClubCount(container);

    if (!select || !message) {
      return;
    }

    if (select.value === STANDARD_VALUE) {
      message.textContent = '';
      message.classList.remove('is-chargeable');
      return;
    }

    const totalCharge = clubCount * 5;
    const clubLabel = clubCount === 1 ? 'club' : 'clubs';

    message.textContent =
      `${select.value}: £${totalCharge.toFixed(2)} ` +
      `(${clubCount} ${clubLabel} at £5 each).`;

    message.classList.add('is-chargeable');
  }

  function initialiseMessages(root = document) {
    root
      .querySelectorAll(SELECTORS.adjustment)
      .forEach((container) => {
        updateMessage(container);
      });
  }

  async function addItems(items) {
    const response = await fetch(
      `${getCartRoot()}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      }
    );

    let result;

    try {
      result = await response.json();
    } catch {
      result = null;
    }

    if (!response.ok) {
      throw new Error(
        result?.description ||
        result?.message ||
        'The products could not be added to the basket.'
      );
    }

    return result;
  }

  function setSubmitting(form, isSubmitting) {
    const button = form.querySelector(
      SELECTORS.submitButton
    );

    if (!button) {
      return;
    }

    button.disabled = isSubmitting;
    button.setAttribute(
      'aria-busy',
      String(isSubmitting)
    );

    if (isSubmitting) {
      if (!button.dataset.originalText) {
        button.dataset.originalText =
          button.textContent.trim();
      }

      button.textContent = 'Adding…';
      return;
    }

    if (button.dataset.originalText) {
      button.textContent =
        button.dataset.originalText;

      delete button.dataset.originalText;
    }
  }

  function isMaximumQuantityError(message) {
    const normalisedMessage = message.toLowerCase();

    return (
      normalisedMessage.includes('maximum quantity') ||
      normalisedMessage.includes(
        'already in your cart'
      ) ||
      normalisedMessage.includes(
        'already in your basket'
      ) ||
      normalisedMessage.includes(
        'all items are in your cart'
      ) ||
      normalisedMessage.includes(
        'all items are in your basket'
      )
    );
  }

  function isInsufficientStockError(message) {
    const normalisedMessage = message.toLowerCase();

    return (
      normalisedMessage.includes(
        'not enough items available'
      ) ||
      normalisedMessage.includes(
        'not enough stock'
      ) ||
      normalisedMessage.includes(
        'available quantity'
      ) ||
      normalisedMessage.includes('sold out')
    );
  }

  function showCartInventoryError() {
    window.alert(
      'This item is already in your basket and no more stock is currently available. ' +
      'Please review the item in your basket before continuing.'
    );

    window.location.assign(
      `${getCartRoot()}cart`
    );
  }

  function showGeneralInventoryError() {
    window.alert(
      'The requested quantity is not currently available. ' +
      'Please reduce the quantity or review your basket.'
    );
  }

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const select = event.target.closest(
      SELECTORS.select
    );

    if (!select) {
      return;
    }

    const container = select.closest(
      SELECTORS.adjustment
    );

    if (container) {
      updateMessage(container);
    }
  });

  document.addEventListener(
    'submit',
    async (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const form = event.target.closest(
        SELECTORS.productForm
      );

      if (!form) {
        return;
      }

      const adjustment = form.querySelector(
        SELECTORS.adjustment
      );

      if (!adjustment) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const lieAngleSelect =
        adjustment.querySelector(
          SELECTORS.select
        );

      if (!lieAngleSelect) {
        return;
      }

      try {
        const lieAngle = lieAngleSelect.value;
        const clubCount =
          getClubCount(adjustment);

        const adjustmentVariantId =
          getAdjustmentVariantId(adjustment);

        const productQuantity =
          getQuantity(form);

        const mainVariantId =
          getVariantId(form);

        const reference =
          createReference();

        const productProperties = {
          ...getProperties(form),
          'Lie angle': lieAngle
        };

        const items = [];

        if (lieAngle !== STANDARD_VALUE) {
          if (!adjustmentVariantId) {
            throw new Error(
              'The lie-angle adjustment variant has not been configured.'
            );
          }

          productProperties._lie_angle_reference =
            reference;

          productProperties._club_count =
            String(clubCount);
        }

        items.push({
          id: mainVariantId,
          quantity: productQuantity,
          properties: productProperties
        });

        if (lieAngle !== STANDARD_VALUE) {
          items.push({
            id: adjustmentVariantId,
            quantity:
              clubCount * productQuantity,
            properties: {
              'Lie angle': lieAngle,
              '_lie_angle_reference':
                reference,
              '_adjustment_for_variant':
                String(mainVariantId)
            }
          });
        }

        setSubmitting(form, true);

        await addItems(items);

        window.location.assign(
          `${getCartRoot()}cart`
        );
      } catch (error) {
        console.error(
          'Lie-angle add-to-cart error:',
          error
        );

        const message =
          error instanceof Error
            ? error.message
            : 'The product could not be added to the basket.';

        if (isMaximumQuantityError(message)) {
          showCartInventoryError();
          return;
        }

        if (isInsufficientStockError(message)) {
          showGeneralInventoryError();
          setSubmitting(form, false);
          return;
        }

        window.alert(message);
        setSubmitting(form, false);
      }
    },
    true
  );

  initialiseMessages();

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseMessages(event.target);
    }
  );
})();